import React, { useCallback, useEffect, useMemo, useState } from 'react';

const envOrigin = (import.meta.env.VITE_API_ORIGIN || '').trim().replace(/\/$/, '');
const isPages = typeof window !== 'undefined' && /\.pages\.dev$/i.test(window.location?.hostname || '');
const API_BASE = isPages ? '' : (envOrigin || '');
const api = (path) => (API_BASE ? API_BASE + (path.startsWith('/') ? path : `/${path}`) : path);

const MARS_URL = 'https://project-mars.app/';
const PRESETS = [
  { id: 'ge5', label: '5 级以上可挖', minLevel: 5 },
  { id: 'all', label: '全部可挖', minLevel: 1 },
  { id: 'ge10', label: '10 级以上可挖', minLevel: 10 },
];

function formatTime(ms) {
  if (!Number.isFinite(ms) || !ms) return '-';
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

function padParcel(id) {
  return String(id).padStart(4, '0');
}

export default function MarsMiningPage() {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [minLevel, setMinLevel] = useState(5);
  const [copied, setCopied] = useState('');
  const [presetId, setPresetId] = useState('ge5');

  const loadBoard = useCallback(async ({ force = false } = {}) => {
    setLoading(true);
    setError('');
    try {
      const qs = force ? '?force=1' : '';
      const resp = await fetch(api(`/api/mars-plots${qs}`), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) {
        throw new Error(body?.message || `读取盘面失败 ${resp.status}`);
      }
      setBoard(body.data || null);
    } catch (err) {
      setError(err?.message || '读取盘面失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  const applyPreset = useCallback((preset) => {
    setPresetId(preset.id);
    setMinLevel(preset.minLevel);
  }, []);

  const plots = Array.isArray(board?.plots) ? board.plots : [];
  const filtered = useMemo(() => {
    const lv = Math.max(1, Math.min(20, Number(minLevel) || 1));
    return plots
      .filter((row) => {
        if (row.burned) return false;
        if (!row.empty) return false;
        if (Number(row.level) < lv) return false;
        if (board?.openKnown && row.open !== true) return false;
        return true;
      })
      .sort((a, b) => Number(b.level) - Number(a.level) || Number(a.parcel) - Number(b.parcel));
  }, [plots, minLevel, board?.openKnown]);

  const copyText = useCallback(async (text, hint) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(hint);
      window.setTimeout(() => setCopied(''), 1600);
    } catch {
      setCopied('复制失败，请手动选中编号');
    }
  }, []);

  const copyAll = useCallback(() => {
    const ids = filtered.map((row) => String(row.parcel)).join('\n');
    if (!ids) return;
    copyText(ids, `已复制 ${filtered.length} 个编号`);
  }, [filtered, copyText]);

  const openKnown = board?.openKnown !== false;
  const openEmpty = Number(board?.openEmpty) || 0;

  return (
    <div className="volume-spike-page primary-arb-page mars-mining-page">
      <section className="cxmt-hero">
        <div>
          <span className="cxmt-eyebrow">Project Mars</span>
          <h1>火星挖矿</h1>
          <p>
            同步全火星 5000 块地，按官方规则筛「可种机」地块：开门给其他玩家，且这一格是空的。
            默认看 5 级以上。复制编号后到官网右下角 Find plot 跳转。槽位变得很快，用之前再同步一次。
          </p>
        </div>
        <div className="cxmt-hero-actions">
          <a href={MARS_URL} target="_blank" rel="noreferrer">打开官网</a>
          <button type="button" disabled={loading} onClick={() => loadBoard({ force: true })}>
            {loading ? '同步中…' : '同步盘面'}
          </button>
        </div>
      </section>

      <section className="volume-spike-panel volume-spike-cloud">
        <div className="volume-spike-panel-head">
          <div>
            <h2>当前盘面</h2>
            <p>
              {board?.fetchedAt ? `更新于 ${formatTime(board.fetchedAt)}` : '页面先出来，再拉盘面，不会把 5000 块地塞进首屏。'}
              {board?.cached ? '（缓存约 20 秒）' : ''}
            </p>
          </div>
          <span className={`volume-spike-badge tone-${loading ? 'warm' : error ? 'bad' : 'ok'}`}>
            {loading ? '同步中' : error ? '失败' : board ? '已同步' : '等待'}
          </span>
        </div>
        {error ? (
          <div className="volume-spike-empty">{error}</div>
        ) : (
          <div className="volume-spike-cloud-grid">
            <div>
              <span>全火星</span>
              <strong>{board?.total ?? '-'}</strong>
              <small>块地</small>
            </div>
            <div>
              <span>空地</span>
              <strong>{board?.empty ?? '-'}</strong>
              <small>没有矿机</small>
            </div>
            <div>
              <span>开门空地</span>
              <strong>{openKnown ? openEmpty : '未知'}</strong>
              <small>{openKnown ? '可种机候选' : (board?.openError || '开门状态未读到')}</small>
            </div>
            <div>
              <span>当前筛选</span>
              <strong>{board ? filtered.length : '-'}</strong>
              <small>等级 ≥ {Math.max(1, Number(minLevel) || 1)}</small>
            </div>
          </div>
        )}
      </section>

      <section className="cxmt-config volume-spike-config volume-spike-config-6">
        <div>
          <span>筛选预设</span>
          <div className="mars-preset-row">
            {PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={presetId === preset.id ? 'is-active' : ''}
                onClick={() => applyPreset(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <span>最低等级</span>
          <input
            type="number"
            min="1"
            max="20"
            value={minLevel}
            onChange={(e) => {
              setPresetId('');
              setMinLevel(e.target.value);
            }}
          />
          <small>只列出空地；开门状态可读时再要求开门</small>
        </div>
      </section>

      <section className="volume-spike-panel">
        <div className="volume-spike-panel-head">
          <div>
            <h2>可挖地块</h2>
            <p>一格同时只能一台机。复制编号，去官网右下角 Find plot 输入后跳转。</p>
          </div>
          <div className="mars-list-actions">
            {copied ? <em className="volume-spike-badge tone-ok">{copied}</em> : null}
            <button type="button" disabled={!filtered.length} onClick={copyAll}>
              复制全部编号
            </button>
          </div>
        </div>
        {!board && !error ? (
          <div className="volume-spike-empty">正在读取盘面…</div>
        ) : filtered.length === 0 ? (
          <div className="volume-spike-empty">当前筛选没有可挖地。槽位变化很快，可以再点一次同步。</div>
        ) : (
          <div className="bem-miner-table-wrap">
            <table className="bem-miner-table">
              <thead>
                <tr>
                  <th>编号</th>
                  <th>等级</th>
                  <th>开门</th>
                  <th>格子</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.parcel}>
                    <td>#{padParcel(row.parcel)}</td>
                    <td>Lv {row.level}</td>
                    <td>{row.open === true ? '开门' : row.open === false ? '关门' : '未知'}</td>
                    <td>{row.empty ? '空着' : '有机'}</td>
                    <td>
                      <button
                        type="button"
                        className="mars-copy-btn"
                        onClick={() => copyText(String(row.parcel), `已复制 #${row.parcel}`)}
                      >
                        复制
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
